// ========================================
// FINAL WORKING MobileFilters.js - Fixed Clear Button
// ========================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Col } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'components/dashboards/manage-projects/DatePicker';
import { debounce } from 'lodash';

const MobileFilters = ({
  showFilters,
  globalSearch,
  dateFilter,
  dropdownFilter,
  dropdownData = [],
  dropdownOptions = {},
  selectedValues = {},
  setSelectedValues,
  globalfilters = { search_text: '', start_date: '', end_date: '' }, // Safe default
  handleGlobalSearch,
  handleDateChange,
  customStyles = {},
  clearAllDates
}) => {
  // Local search state to handle typing properly
  const [searchValue, setSearchValue] = useState('');

  // Update local search when global filters change
  useEffect(() => {
    setSearchValue(globalfilters?.search_text || '');
  }, [globalfilters?.search_text]);

  // ===== DEBOUNCED SEARCH HANDLER =====
  const debouncedSearch = useMemo(
    () =>
      debounce(value => {
        if (handleGlobalSearch) {
          handleGlobalSearch(value);
        }
      }, 500), // 500ms debounce delay
    [handleGlobalSearch]
  );

  // ===== SEARCH CHANGE HANDLER WITH DEBOUNCE =====
  const handleSearchChange = useCallback(
    e => {
      const value = e.target.value;
      setSearchValue(value);

      // Call debounced search
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  // ===== DATE PARSING (EXACT SAME AS TABLEHEADER) =====
  const parseDateToJSDate = useCallback(dateStr => {
    if (!dateStr) return null;

    if (typeof dateStr === 'string') {
      // Split by '-' and handle YYYY-MM-DD format (same as TableHeader)
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts;
        const parsedDate = new Date(
          parseInt(year),
          parseInt(month, 10) - 1,
          parseInt(day)
        );
        return parsedDate;
      }
    }

    // Return date object if already a Date
    if (dateStr instanceof Date && !isNaN(dateStr.getTime())) {
      return dateStr;
    }
    return null;
  }, []);

  // ===== DATE CHANGE HANDLER (EXACT SAME AS TABLEHEADER) =====
  const handleDateRangeChange = useCallback(
    (date, field) => {
      // Ensure date is a Date object (same logic as TableHeader)
      let processedDate = date;

      if (typeof date === 'string') {
        processedDate = new Date(date);
      }

      if (
        processedDate &&
        processedDate instanceof Date &&
        !isNaN(processedDate)
      ) {
        if (handleDateChange) {
          handleDateChange(processedDate, field); // Pass the Date object
        }
      } else {
        if (handleDateChange) {
          handleDateChange(null, field);
        }
      }
    },
    [handleDateChange]
  );

  // ===== IMPROVED CLEAR ALL DATES HANDLER WITH SEQUENTIAL CLEARING =====
  const handleClearDates = useCallback(() => {
    // If parent provides a direct clear function, use it
    if (clearAllDates) {
      clearAllDates();
    } else {
      // Fallback to individual calls
      if (handleDateChange) {
        handleDateChange(null, 'start_date');
        handleDateChange(null, 'end_date');
      }
    }
  }, [clearAllDates, handleDateChange]);

  // ===== GET CURRENT DATE VALUES (DIRECT ACCESS TO ACTUAL VALUES) =====
  const startDateValue = useMemo(() => {
    // Try to get the actual date value from globalfilters
    const dateValue =
      globalfilters?.start_date || globalfilters?.dateRange?.start_date;
    // If dateValue is empty string or null/undefined, return null
    if (!dateValue || dateValue === '') {
      return null;
    }

    const parsedDate = parseDateToJSDate(dateValue);
    return parsedDate;
  }, [
    globalfilters?.start_date,
    globalfilters?.dateRange?.start_date,
    parseDateToJSDate
  ]);

  const endDateValue = useMemo(() => {
    // Try to get the actual date value from globalfilters
    const dateValue =
      globalfilters?.end_date || globalfilters?.dateRange?.end_date;
    // If dateValue is empty string or null/undefined, return null
    if (!dateValue || dateValue === '') {
      return null;
    }

    const parsedDate = parseDateToJSDate(dateValue);
    return parsedDate;
  }, [
    globalfilters?.end_date,
    globalfilters?.dateRange?.end_date,
    parseDateToJSDate
  ]);

  // ===== CHECK IF ANY DATES ARE SELECTED =====
  const hasSelectedDates = useMemo(() => {
    const hasStart = !!(
      globalfilters?.start_date || globalfilters?.dateRange?.start_date
    );
    const hasEnd = !!(
      globalfilters?.end_date || globalfilters?.dateRange?.end_date
    );
    return hasStart || hasEnd;
  }, [
    globalfilters?.start_date,
    globalfilters?.dateRange?.start_date,
    globalfilters?.end_date,
    globalfilters?.dateRange?.end_date
  ]);

  // ===== CLEANUP DEBOUNCE ON UNMOUNT =====
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div
      className={`d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 ${
        showFilters ? '' : 'd-none d-md-flex'
      }`}
    >
      {/* Global Search Input */}
      {globalSearch && (
        <div className="flex-grow-1 mb-3 mb-md-0 me-md-3">
          <input
            type="text"
            placeholder="Search..."
            className="form-control"
            value={searchValue}
            onChange={handleSearchChange}
          />
        </div>
      )}

      {/* Date and Dropdown Filters */}
      <div className="d-flex flex-column flex-md-row gap-3 w-100 w-md-auto">
        {/* Start Date Picker - EXACT SAME CONFIG AS TABLEHEADER */}
        {dateFilter && (
          <div className="datepicker-wrapper flex-grow-1 flex-md-shrink-0">
            <DatePicker
              selected={startDateValue}
              onChange={date => handleDateRangeChange(date, 'start_date')}
              placeholderText="Start Date"
              className="form-control"
              dateFormat="dd-MM-yyyy"
              maxDate={endDateValue || new Date()}
            />
          </div>
        )}

        {/* End Date Picker - EXACT SAME CONFIG AS TABLEHEADER */}
        {dateFilter && (
          <div className="datepicker-wrapper flex-grow-1 flex-md-shrink-0">
            <DatePicker
              selected={endDateValue}
              onChange={date => handleDateRangeChange(date, 'end_date')}
              placeholderText="End Date"
              className="form-control"
              dateFormat="dd-MM-yyyy"
              minDate={startDateValue}
              maxDate={new Date()}
            />
          </div>
        )}

        {/* Clear Date Filters Button - SAME LOGIC AS TABLEHEADER */}
        {dateFilter && hasSelectedDates && (
          <div className="flex-shrink-0">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleClearDates}
              style={{ whiteSpace: 'nowrap' }}
            >
              Clear
            </button>
          </div>
        )}

        {/* Dropdown Filters */}
        {dropdownFilter && (
          <div className="d-flex flex-wrap gap-3">
            {dropdownData?.map(drop => (
              <div
                key={drop.key}
                className="dropdown-filter flex-grow-1 flex-md-shrink-0"
                style={{ minWidth: '200px' }}
              >
                <Select
                  options={dropdownOptions[drop.key] || []}
                  isClearable={true}
                  placeholder={drop.label}
                  classNamePrefix="react-select"
                  value={
                    drop.isMulti
                      ? (selectedValues[drop.key] || [])
                          .map(v =>
                            dropdownOptions[drop.key]?.find(
                              opt => opt.value === v
                            )
                          )
                          .filter(Boolean)
                      : dropdownOptions[drop.key]?.find(
                          opt => opt.value === selectedValues[drop.key]
                        ) || null
                  }
                  onChange={selected => {
                    const selectedValuesData = drop.isMulti
                      ? selected
                        ? selected.map(item => item.value)
                        : []
                      : selected
                      ? selected.value
                      : null;

                    if (setSelectedValues) {
                      setSelectedValues(prev => ({
                        ...prev,
                        [drop.key]: selectedValuesData
                      }));
                    }
                  }}
                  isMulti={drop.isMulti}
                  closeMenuOnSelect={!drop.isMulti}
                  styles={{
                    ...customStyles,
                    control: base => ({
                      ...base,
                      minWidth: '200px',
                      maxWidth: '400px',
                      width: '100%'
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      color: state.isDisabled ? '#d3d3d3' : '#000',
                      fontWeight: state.isDisabled ? 'lighter' : 'bold',
                      cursor: state.isDisabled ? 'not-allowed' : 'pointer'
                    })
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

MobileFilters.propTypes = {
  showFilters: PropTypes.bool.isRequired,
  globalSearch: PropTypes.bool,
  dateFilter: PropTypes.bool,
  dropdownFilter: PropTypes.bool,
  dropdownData: PropTypes.array,
  dropdownOptions: PropTypes.object,
  selectedValues: PropTypes.object,
  setSelectedValues: PropTypes.func,
  globalfilters: PropTypes.object,
  handleGlobalSearch: PropTypes.func,
  handleDateChange: PropTypes.func,
  clearAllDates: PropTypes.func,
  customStyles: PropTypes.object
};

MobileFilters.defaultProps = {
  globalSearch: false,
  dateFilter: false,
  dropdownFilter: false,
  dropdownData: [],
  dropdownOptions: {},
  selectedValues: {},
  globalfilters: { search_text: '', start_date: '', end_date: '' },
  customStyles: {}
};

export default MobileFilters;
